const API_BASE_URL = '/best3intennis-api/api';

/**
 * Safely parse JSON from server responses, even if preceded/followed by PHP notices or HTML tags.
 */
export const safeParseJson = (text) => {
  if (!text || typeof text !== 'string') return null;
  try {
    return JSON.parse(text);
  } catch (_e) {
    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
      try {
        return JSON.parse(text.substring(firstBrace, lastBrace + 1));
      } catch (_nested) {}
    }
    return null;
  }
};

/**
 * Returns true if the user is currently on the match tracking / recording screen.
 */
export const isMatchRecordingActive = () => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.includes('/match-tracker');
};

/**
 * Handles authentication errors (e.g. 401 Unauthorized).
 * Guarantees that users actively recording match results are NEVER logged out or redirected.
 */
export const handleAuthError = (navigate) => {
  if (isMatchRecordingActive()) {
    console.warn('Suppressing auth logout: user is actively on the match tracking screen.');
    return;
  }
  localStorage.removeItem('token');
  if (navigate) {
    navigate('/');
  } else if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
};

/**
 * Checks if an API response contains a refreshed token and saves it immediately.
 */
export const saveTokenIfPresent = (data) => {
  if (data && data.token) {
    localStorage.setItem('token', data.token);
  }
};

/**
 * Silently refreshes the JWT token with the backend, extending the session by 12 hours.
 */
export const refreshToken = async () => {
  try {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return null;

    const response = await fetch(`${API_BASE_URL}/refresh_token.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
      },
    }).catch(() => null);

    if (response && response.ok) {
      const rawText = await response.text();
      const data = safeParseJson(rawText);
      if (data && data.token) {
        localStorage.setItem('token', data.token);
        if (data.user) {
          try {
            const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
            localStorage.setItem('user', JSON.stringify({ ...existingUser, ...data.user }));
            window.dispatchEvent(new Event('userUpdated'));
          } catch (_e) {}
        }
        return data.token;
      }
    }
  } catch (error) {
    // Silently ignore background token refresh failures
  }
  return null;
};

/**
 * Resilient authenticated fetch for match tracker that auto-refreshes tokens on 401 and prevents session loss.
 */
export const trackerFetch = async (endpointOrUrl, options = {}) => {
  const url = endpointOrUrl.startsWith('http') ? endpointOrUrl : `${API_BASE_URL}/${endpointOrUrl}`;
  let token = localStorage.getItem('token');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    let response = await fetch(url, { ...options, headers }).catch((err) => {
      return { ok: false, status: 0, error: err, text: async () => '' };
    });

    if (!response || !response.text) {
      return { ok: false, status: 0, data: null };
    }

    // On 401, attempt silent refresh once if not refreshing
    if (response.status === 401 && !url.includes('refresh_token.php')) {
      const newToken = await refreshToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers }).catch((err) => {
          return { ok: false, status: 0, error: err, text: async () => '' };
        });
      }
    }

    const rawText = await response.text();
    const data = safeParseJson(rawText);
    if (data) {
      saveTokenIfPresent(data);
    }
    return { ok: Boolean(response.ok), status: response.status || 0, data };
  } catch (err) {
    return { ok: false, status: 0, error: err, data: null };
  }
};
